import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { getCalendarEvents } from './AppFutureEvents.helper'
import {
  CalendarEventViewPreference,
  useGetUserCalendarEventsQuery,
} from '__generated__/api-types-and-hooks'
import { useTenantId } from 'utils/useTenantId'
import { IUpcomingEvent } from 'types'
import { USER_ROLES } from 'config'
import AppDashboardUpcomingEvents from '../AppDashboardUpcomingEvents'
import AppAdvisorDashboardUpcomingEvents from 'components/AppAdvisorDashboard/AppAdvisorPlays/AppAdvisorDashboardUpcomingEvents'
import { RootState } from 'App'
import { useSelector } from 'react-redux'
import moment from 'moment-timezone'

interface IFutureEventsPayload {
  calendarEventViewPreference: string
  role: string[]
  showInternalMeetingsOnly?: boolean
}

const AppFutureEvents = ({
  calendarEventViewPreference,
  role,
  showInternalMeetingsOnly,
}: IFutureEventsPayload) => {
  const { t } = useTranslation()
  const tenantId = useTenantId()
  const { clientId } = useParams()
  const user = useSelector((state: RootState) => state.user.user)
  const startOfDay = user?.timeZone
    ? moment().tz(user.timeZone).startOf('day').toISOString()
    : moment().startOf('day').toISOString()
  const endOfDay = user?.timeZone
    ? moment().tz(user.timeZone).endOf('day').toISOString()
    : moment().endOf('day').toISOString()
  const { data, isFetching } = useGetUserCalendarEventsQuery(
    {
      calendarName:
        calendarEventViewPreference === CalendarEventViewPreference.Internal
          ? CalendarEventViewPreference.Internal
          : '',
      tenantId,
      timeMin: startOfDay,
      timeMax: endOfDay,
    },
    { staleTime: 60000, refetchOnWindowFocus: false }
  )

  const { futureMeetings } = getCalendarEvents(data?.getUserCalendarEvents?.data)
  /** These are events parsed into the interface required by the UpcomingEvents component (only meetings that haven't started yet) */
  const eventSummaries: IUpcomingEvent[] = useMemo(() => {
    const upcoming = futureMeetings ?? []
    return upcoming.length > 0
      ? upcoming.map((event) => {
          const upcomingEvent: IUpcomingEvent = {
            description: event.title,
            date: event.startDateTime,
            endDate: event.end,
            eventId: event.eventId,
            eventType: event.eventType,
            businessName: event.businessName,
            businessLogo: event.businessLogo,
          }
          return upcomingEvent
        })
      : []
  }, [futureMeetings])

  const noEventMessage =
    role?.length && role.includes(USER_ROLES.BUSINESS_OWNER)
      ? t('calendar.scheduleSessionAdvisor')
      : t('commandCenter.getStartedOnCalendar')

  return (
    <>
      {role?.includes(USER_ROLES.BUSINESS_OWNER) || clientId ? (
        <AppDashboardUpcomingEvents
          events={eventSummaries}
          noEventsMessage={noEventMessage}
          loading={isFetching}
          showInternalMeetingsOnly={showInternalMeetingsOnly}
        />
      ) : (
        <AppAdvisorDashboardUpcomingEvents
          events={eventSummaries}
          noEventsMessage={noEventMessage}
          loading={isFetching}
          showInternalMeetingsOnly={showInternalMeetingsOnly}
        />
      )}
    </>
  )
}

export default AppFutureEvents
